/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

/* the dispatch logic to use */

import {Logger} from "@/util/logger";

export enum SimulationDispatchType {
  WORKBLOX,
  RCM,
  UNCONSTRAINED
}

/* the logging level (verbosity) */
export enum SimulationLogType {
  DEBUG,
  INFO,
  NOTICE,
  WARNING,
  ERROR,
  CRITICAL,
  ALERT,
  EMERGENCY
}


/**
 *
 * @class SimConfig
 *
 * @brief simple simulation configuration, covers the basic parameters for launching a simulation job.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class SimConfig {

  /** which dispatch logic to use */
  public      dispatch: SimulationDispatchType = SimulationDispatchType.WORKBLOX;

  /** maximum simulation days */
  public       maxDays: number                 = 30;

  /** maximum wall clock runtime for simulator (default is 1 hour or 3600 seconds) */
  public       maxTime: number                 = 3600;

  /** maximum memory usage by the simulator (default is 6GB of RAM) */
  public       maxMemory: number               = 1024*1024*1024*6;

  /** how long (hours) to wait before deadlock detection */
  public   lockupHours: number                 = 720;

  /** logging verbosity level */
  public      logLevel: SimulationLogType      = SimulationLogType.ERROR;

  /** run in permissive mode? */
  public    permissive: boolean                = true;

  /** priority when queued */
  public      priority: number                 = 1.0;

  /** automatically show the stringline viewer? */
  public      autoview: boolean                = true;

  /** the user log storage target (normally MongoDB) */
  public       userlog: string                 = '';

  /** the user log window start time */
  public    traceStart: string                 = '';

  /** the user log window end time */
  public      traceEnd: string                 = '';

  /** passenger train reaction time (seconds) */
  public passengerReactionTime: number         = 0;

  /** freight train reaction time (seconds) */
  public freightReactionTime: number           = 0;

  /** the remote line simulator we use for actually simulating */
  public      endpoint: string|null            = null;

  /** the name/tag for the remote line simulation being used */
  public      engine: string                   = '';

  /**
   * the mapping of remote simulation engine to the URL for each one.  Can be
   * overridden with the SIMULATION_ENGINES environment variable (via providing it
   * to the overrideEndpoints() method.
   *
   */
  public static urlMap: {name: string, url: string}[] = [
    {
      name: 'linesim',
      url: 'https://linesim.sn.ironroad.ca'
    },
    {
      name: 'cloudsim',
      url: 'https://cloudsim.sn.ironroad.ca'
    },
    {
      name: 'linesimbeta',
      url: 'https://linesimbeta.sn.ironroad.ca'
    }
  ];

  public static engines(): string[] {

    return SimConfig.urlMap.map(x => x.name);
  }

  /** standard constructor */
  public constructor(init?: Partial<SimConfig>) {

    if (typeof init !== 'undefined') {

      if (init?.logLevel !== null) {

        if (typeof init.logLevel === 'string') {

          init.logLevel = SimConfig.logLevelType(init.logLevel);
        }
      }

      if (init?.dispatch !== null) {

        if (typeof init.dispatch === 'string') {

          init.dispatch = SimConfig.dispatchType(init.dispatch);
        }
      }
    }

    (<any>Object).assign(this, init);
  }

  /** convert to proper dispatch enum */
  public static dispatchType(value: string): SimulationDispatchType {

    value = value.trim().toLowerCase();

    switch (value) {
      case 'workblox':      return SimulationDispatchType.WORKBLOX;
      case 'rcm':           return SimulationDispatchType.RCM;
      case 'unconstrained': return SimulationDispatchType.UNCONSTRAINED;
    }

    return SimulationDispatchType.WORKBLOX;
  }

  public static dispatchString(value: SimulationDispatchType): string {

    switch (value) {

      case SimulationDispatchType.WORKBLOX:      return 'workblox';
      case SimulationDispatchType.RCM:           return 'rcm';
      case SimulationDispatchType.UNCONSTRAINED: return 'unconstrained';
    }

    return 'workblox';
  }

  /** convert to proper log level enum */
  public static logLevelType(value: string): SimulationLogType {

    value = value.trim().toLowerCase();

    switch (value) {
      case 'debug':     return SimulationLogType.DEBUG;
      case 'info':      return SimulationLogType.INFO;
      case 'notice':    return SimulationLogType.NOTICE;
      case 'warning':   return SimulationLogType.WARNING;
      case 'error':     return SimulationLogType.ERROR;
      case 'critical':  return SimulationLogType.CRITICAL;
      case 'alert':     return SimulationLogType.ALERT;
      case 'emergency': return SimulationLogType.EMERGENCY;
    }

    return SimulationLogType.ERROR;
  }

  public static logLevelString(value: SimulationLogType): string {

    switch (value) {

      case SimulationLogType.DEBUG:     return 'debug';
      case SimulationLogType.INFO:      return 'info';
      case SimulationLogType.NOTICE:    return 'notice';
      case SimulationLogType.WARNING:   return 'warning';
      case SimulationLogType.ERROR:     return 'error';
      case SimulationLogType.CRITICAL:  return 'critical';
      case SimulationLogType.ALERT:     return 'alert';
      case SimulationLogType.EMERGENCY: return 'emergency';
    }

    return 'error';
  }

  /**
   * all implementations must return a simplified version of themselves, that is appropriate for use
   * with JSON.stringify().
   */
  toJSON(init?: any): any  {

    const base = {
      dispatch:                this.dispatch,
      maxdays:                 this.maxDays,
      lockuphours:             this.lockupHours,
      loglevel:                this.logLevel,
      permissive:              this.permissive,
      priority:                this.priority,
      userlog:                 this.userlog,
      trace_start:             this.traceStart,
      trace_end:               this.traceEnd,
      passenger_reaction_time: this.passengerReactionTime,
      freight_reaction_time:   this.freightReactionTime,
      endpoint:                this.endpoint
    };

    return init ? {...init, ...base } : base;
  }
  /**
   * all implementations must be able to recover themselves from the given object which was
   * deserialized via JSON.parse().
   */
  fromJSON(json: any) {

    this.dispatch                = json.dispatch;
    this.maxDays                 = json.maxdays;
    this.lockupHours             = json.lockuphours;
    this.logLevel                = json.loglevel;
    this.permissive              = json.permissive;
    this.priority                = json.priority;
    this.userlog                 = json.userlog;
    this.traceStart              = json.trace_start;
    this.traceEnd                = json.trace_end;
    this.endpoint                = json.endpoint;
    this.passengerReactionTime   = 0;
    this.freightReactionTime     = 0;

    if (typeof json.passenger_reaction_time != 'undefined') {

      this.passengerReactionTime = json.passenger_reaction_time;
    }

    if (typeof json.freight_reaction_time != 'undefined') {

      this.freightReactionTime   = json.freight_reaction_time;
    }

  }

  /**
   * override one or more of the simulation engine endpoints.  You can also use this method to add
   * new additional endpoints.
   *
   * @param urlMap a comma separated list of simulation engine tag to URL
   * strings.  For example: linesim@http://linesim,cloudsim@http://cloudsim,linesimbeta@http://linesimbeta
   *
   */
  static overrideEndpoints(urlMap: string): void {

    let items: string[] = urlMap.split(/[, ]+/);

    /* first look for overrides */

    let found: object = {};

    for (let filtered of SimConfig.urlMap) {

      for (let item of items) {

        /* try to parse this endpoint mapping... */

        let matches: RegExpMatchArray | null = item.match(/^([^@]+)@([^@]+)$/);

        if (!matches) {

          Logger.error(`[SimConfig] overrideEndpoints, bad engine mapping ${item}`);

          continue;
        }

        if (matches[1] == filtered.name) {

          /* override */

          filtered.url      = matches[2];

          found[matches[1]] = true;
        }

      }

    } /* end of override search */

    /* now add in any new ones that aren't an override */

    for (let item of items) {

      let matches: RegExpMatchArray | null = item.match(/^([^@]+)@([^@]+)$/);

      if (!matches) {

        Logger.error(`[SimConfig] overrideEndpoints, bad engine mapping ${item}`);

        continue;
      }

      if (found.hasOwnProperty(matches[1])) {

        /* it was an override */

        continue;
      }

      /* this is a completely new one. */

      SimConfig.urlMap.push({
        name: matches[1],
        url:  matches[2]
      });

    } /* end of adding new endpoints */

  }

  /** fetch a list of the possible endpoints for simulators (suitable for a pick list) */
  static endpoints(): {name: string, url: string}[] {

    /* clone it in case they modify it, our definitions is ours, they shouldn't be changing it. */

    return [...SimConfig.urlMap];
  }

  /** fetch the remote simulation base URL for the given simulation engine (by name/tag) */
  static endpointOfEngine(tag: string): string|null {

    return SimConfig.urlMap.find(x => x.name == tag)?.url;
  }

}
